import { useCallback } from 'react';
import { api } from 'src/constants/apiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { Message } from './useGetMessagesGeneric';

interface MessageRecipientPairDto {
  MsgId: number;
  RecipientClientId: number;
}

interface MessageIdCount {
  messageId: number;
  clientId?: string;
  recipient?: string;
  rowId?: string;
}

interface DeleteMessageRequest {
  clientId: string;
  messageIds: number[];
  isDeleteAction: boolean;
  origin?: string;
  messages?: Message[];
}

interface UseDeleteMessagesApiReturn {
  isLoading: boolean;
  deleteMessage: (params?: DeleteMessageRequest) => Promise<any>;
  deleteMessageStatus: 'idle' | 'pending' | 'success' | 'error';
}

const useDeleteMessagesApi = (
  clientId: string,
  messageIds: number[],
  isDeleteAction: boolean,
  origin: string,
  messages?: Message[]
): UseDeleteMessagesApiReturn => {
  
      const call = useCallback(
          async (params?: DeleteMessageRequest) => {
            try {
                const messageIdsToProcess = params?.messageIds || messageIds;
                const actionToTake = params?.isDeleteAction ?? isDeleteAction;
                const messageObjects = params?.messages || messages || [];
                const originToUse = params?.origin || origin || 'outbox';
                                             
                // Check for duplicate messageIds but different clientIds
                const messageIdCounts: Record<number, MessageIdCount[]> = {};
                messageObjects.forEach((msg: Message) => {
                  const id = msg.messageId;
                  if (!messageIdCounts[id]) {
                    messageIdCounts[id] = [];
                  }
                  messageIdCounts[id].push({
                    messageId: msg.messageId,
                    clientId: msg.clientId,
                    recipient: msg.recipient,
                    rowId: msg.rowId
                  });
                });
                
                const msgIdRecipientPairs: MessageRecipientPairDto[] = [];
                
                // If multiple message objects, process directly instead of trying to match by messageId
                if (messageObjects && messageObjects.length > 0) {
                  messageObjects.forEach((messageObj: Message) => {                    
                    let recipientClientId: number;
                    if (messageObj.clientId) {
                      const rawClientId = messageObj.clientId;
                      recipientClientId = parseInt(String(rawClientId));
                    } else {
                      recipientClientId = 0;  //fallback (dummy) recipientClientId
                    }
                    
                    const pair = {
                      MsgId: messageObj.messageId,
                      RecipientClientId: recipientClientId
                    };
                    
                    msgIdRecipientPairs.push(pair);
                  });
                } else {
                  // Fallback to original logic if no message objects
                  for (let i = 0; i < messageIdsToProcess.length; i++) {
                    const msgId = messageIdsToProcess[i];
                    const pair = {
                      MsgId: msgId,
                      RecipientClientId: parseInt(clientId)
                    };
                    msgIdRecipientPairs.push(pair);
                  }
                }            
                
                // Use clientId param passed to hook
                const clientIdToUse = params?.clientId || clientId;                
                const apiUrl = api.deleteUndeleteMessage(clientIdToUse, actionToTake, originToUse);
                                
                const res = await axiosInstance.post(
                  apiUrl,
                  msgIdRecipientPairs
                );
                
                return res.data;
            } catch (error) {
              console.error('DeleteMessage API - Error:', error);
              throw error;
            }
          },
          [clientId, messageIds, isDeleteAction, origin, messages]
        );

        const { isLoading, execute, status } = useAsync(call, false);

        return {
          isLoading,
          deleteMessage: execute,
          deleteMessageStatus: status,
        };
};

export default useDeleteMessagesApi;
